import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { useTheme } from "../context/ThemeContext";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SITE_KEY = "0x4AAAAAAFB5K-8wY62HqZqN";

let scriptPromise = null;
function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = () => {
        // Without resetting this, a single failed load (network blip,
        // ad-blocker) permanently wedges the widget: the promise stays
        // rejected forever, so every later mount across the whole session
        // (Login, Profile's reset/email-change modals) gets back the same
        // dead promise and never renders again — silently locking the user
        // out of every captcha-gated auth action until a full page reload.
        scriptPromise = null;
        // Otherwise every retry (manual click, or a later mount elsewhere)
        // leaves the previous failed attempt's dead <script> tag sitting in
        // <head> forever — a small but unbounded-if-repeated DOM leak.
        script.remove();
        reject(new Error("Failed to load the Turnstile script"));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

// Cloudflare's bot-check widget. Supabase's server-side CAPTCHA protection
// rejects signInWithPassword/signInWithOtp calls that don't carry a fresh
// captchaToken (updateUser is NOT captcha-gated — no call site passes it a
// token, and none needs to, since every place that calls it is already on
// an authenticated session), so every public sign-in/sign-up/OTP-send entry
// point needs one of these mounted. Tokens are single-use — call
// ref.reset() after each submit (success or failure) to get a new one for
// the next attempt.
const Turnstile = forwardRef(function Turnstile({ onVerify, onExpire }, ref) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifyRef = useRef(onVerify);
  const onExpireRef = useRef(onExpire);
  const [loadError, setLoadError] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const themeCtx = useTheme();
  // Captured once per mount, deliberately not reactive: this component
  // remounts fresh every time its parent modal opens, which already picks
  // up whatever theme is current then — the only thing reacting live would
  // add is an OS-level prefers-color-scheme auto-switch (e.g. a scheduled
  // sunset dark mode) firing while a captcha-gated modal is already open,
  // silently remounting the widget and invalidating an already-solved token
  // via the cleanup below, with no user-visible explanation. The app's own
  // in-app theme toggle can't reach this case anyway — the settings menu
  // that controls it is unreachable while these modals are open.
  const widgetThemeRef = useRef(themeCtx?.theme === "dark" ? "dark" : "light");

  useEffect(() => {
    onVerifyRef.current = onVerify;
    onExpireRef.current = onExpire;
  });

  useEffect(() => {
    let cancelled = false;
    setLoadError(false);

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          // Follows the app's own light/dark toggle (ThemeContext) rather
          // than Cloudflare's OS-level "auto" — a user who's manually
          // switched the app's theme independent of their OS/browser
          // otherwise sees the widget render in the wrong color scheme.
          theme: widgetThemeRef.current,
          // Cloudflare's default ("auto") silently re-executes an unattended
          // widget and re-invokes onVerify with a fresh token roughly every 5
          // minutes. Some callers (Profile.jsx's password-reset "verify"
          // stage) wire onVerify straight to sending a request — with "auto",
          // leaving that screen open after a failed attempt meant it could
          // keep silently re-sending in the background indefinitely. "manual"
          // instead just calls expired-callback (already handled below by
          // clearing the token) and waits for an explicit reset()/re-render.
          "refresh-expired": "manual",
          callback: token => onVerifyRef.current?.(token),
          "expired-callback": () => onExpireRef.current?.(),
          "error-callback": () => onExpireRef.current?.()
        });
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
        // A script-load retry remounts a fresh, unsolved widget — clear
        // whatever token the parent was holding so the UI and the actual
        // captcha state don't silently disagree. Harmless on a true unmount
        // too, since nothing reads the token once this component's gone.
        onExpireRef.current?.();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryTick]);

  useImperativeHandle(ref, () => ({
    reset() {
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    }
  }));

  if (loadError) {
    return (
      <div style={{ margin: "10px 0", fontSize: 13, color: "var(--accent-danger)" }}>
        Security check failed to load.{" "}
        <button
          type="button"
          onClick={() => setRetryTick(t => t + 1)}
          style={{ color: "var(--accent-link)", fontWeight: 700, background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }}
        >
          Retry
        </button>
      </div>
    );
  }

  return <div ref={containerRef} style={{ margin: "10px 0" }} />;
});

export default Turnstile;
